import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getDisplayedUnit } from 'commons/src/commonFunctions';
import TertiaryButton from 'commons/src/components/buttons/TertiaryButton';
import SectionHeader from 'commons/src/components/headers/SectionHeader';
import RadioButtons from 'commons/src/components/input/Radio';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import SensorIcon from 'commons/src/components/sensors/SensorIcon';
import ThresholdSetting from 'commons/src/components/thresholds/ThresholdSetting';
import { sensorFullNameKey, sensorUnits } from 'commons/src/constants';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { ThresholdRange } from 'commons/src/models/commonTypeScript';
import { CustomOptions, ThresholdOptions, ThresholdUpdatePayload } from 'commons/src/models/customThresholdModels';
import { Store } from '../../../reducers';
import styles from './SensorThresholdMgmt.module.scss';
import useSensorThresholdMgmt from './useSensorThresholdMgmt';

export type Props = {
    buttonText: string;
    sensorType: SensorTypes;
    thresholds: {
        customOptions?: CustomOptions;
        ranges: ThresholdRange[];
        unit: keyof typeof sensorUnits;
        thresholdOptions: ThresholdOptions;
    };
    thresholdScope: ThresholdsResourceScope;
    parentThresholdScope?: ThresholdsResourceScope;
    id?: string;
    updateSuccessAction?: (id: string, payload: ThresholdUpdatePayload) => void;
    parentUrl?: string;
};
const SensorThresholdMgmt = ({
    sensorType,
    thresholds,
    thresholdScope,
    buttonText,
    parentThresholdScope,
    id,
    updateSuccessAction,
    parentUrl,
}: Props): ReactElement => {
    const { t: txt } = useTranslation();
    const {
        changeScopeSelection,
        thresholdOptions,
        goToParentThresholds,
        selectedValues,
        selectedScope,
        handleSetSelectedValues,
        handleOnSave,
        isLoading,
        isSaved,
        error,
    } = useSensorThresholdMgmt({
        thresholds,
        thresholdScope,
        sensorType,
        id,
        updateSuccessAction,
        parentUrl,
    });

    const accountCustomThresholdsEnabled = useSelector((state: Store) => state.toggles.accountCustomThresholdsEnabled);

    const radioOptions = [
        {
            value: parentThresholdScope,
            label: `ThresholdSettings.Default_${parentThresholdScope}`,
            customStyle: styles.parentRadioButton,
            ...(accountCustomThresholdsEnabled && {
                buttonChildren: (
                    <div className={styles.editLink}>
                        <TertiaryButton onClick={goToParentThresholds} title="Edit" size="medium" />
                    </div>
                ),
            }),
        },
        { value: thresholdScope, label: `ThresholdSettings.Custom_${thresholdScope}` },
    ];

    const spaceScope = thresholdScope === ThresholdsResourceScope.SPACE;
    const getUnit = (): ReactElement => {
        const unit = getDisplayedUnit(sensorType, thresholds.unit as string);
        return <div className={styles.unit}>({unit})</div>;
    };

    return (
        <div>
            {!spaceScope && (
                <SectionHeader
                    headerType={4}
                    headerName=""
                    customHeader={
                        <>
                            {txt(sensorFullNameKey(sensorType))} {getUnit()}
                        </>
                    }
                    leadingIcon={<SensorIcon sensor={sensorType} />}
                    key={`thresholdHeader${sensorType}`}
                />
            )}
            <p className={styles.description}>{txt(`CustomThresholds.${sensorType}Description`)}</p>
            {parentThresholdScope && (
                <div className={styles.radioButtons}>
                    <RadioButtons
                        row
                        buttons={radioOptions}
                        onChange={changeScopeSelection}
                        labelId={`thresholdHeader${sensorType}`}
                        selectorName="scope"
                        value={selectedScope}
                    />
                </div>
            )}
            <ThresholdSetting
                sensorType={sensorType}
                ranges={thresholds.ranges}
                thresholdOptions={thresholdOptions}
                customOptions={thresholds.customOptions}
                selectedValues={selectedValues}
                setSelectedValues={handleSetSelectedValues}
                isParentScope={selectedScope === parentThresholdScope}
                scopeModified={selectedScope !== thresholds.thresholdOptions.activeScope}
                buttonText={buttonText}
                loading={isLoading}
                onSave={handleOnSave}
                isSaved={isSaved}
                fullwidthButton={spaceScope}
            />
            {error && (
                <div className={styles.error}>
                    <ResponseBox text={`ErrorCodes.${error.error}`} />
                </div>
            )}
        </div>
    );
};

export default SensorThresholdMgmt;
